import type { ReactNode } from 'react'
import { Outlet } from 'react-router-dom'

import { ContentLoading, ForbiddenError } from 'src/components'
import { useWorkspacePermissionsContext } from 'src/hooks'
import {
  hasEveryWorkspacePermission,
  hasSomeWorkspacePermission,
  type WorkspacePermissionCode
} from 'src/utils/workspacePermissions'

type Props = {
  requiredPermission: WorkspacePermissionCode | WorkspacePermissionCode[]
  /** `OR` (default) passes on any code; `AND` requires all of them. */
  mode?: 'AND' | 'OR'
  children?: ReactNode
}

/**
 * Gates a route or a section on workspace permission codes, rendering the
 * "not enough permissions" screen instead when they are missing. Ported from
 * `ebs-platform-web` (`apps/crm/src/components/RequireRoutePermission`).
 *
 * The whole `/workspaces/:id/*` subtree is already gated per sidebar section by
 * `WorkspaceAccessRoute`; use this for finer-grained cases — a nested route or
 * a panel that needs a code its parent section does not.
 */
export const RequireWorkspacePermission = ({
  requiredPermission,
  mode = 'OR',
  children
}: Props) => {
  const { permissions, isLoading } = useWorkspacePermissionsContext()

  // Unresolved permissions deny, so hold the loader rather than flashing a
  // denial the user would see resolve away a moment later.
  if (isLoading || permissions === undefined) {
    return <ContentLoading />
  }

  const codes = Array.isArray(requiredPermission)
    ? requiredPermission
    : [requiredPermission]

  const isAllowed =
    mode === 'AND'
      ? hasEveryWorkspacePermission(permissions, codes)
      : hasSomeWorkspacePermission(permissions, codes)

  if (!isAllowed) {
    return <ForbiddenError />
  }

  return children ?? <Outlet />
}
